import {useTranslation} from '@/translations/i18n.tsx'
import {
    UserContractualDataFormValidationSchema,
    UserExtended,
    UserJobInfoFormSchema,
    UserJobInfoFormValidationSchema,
    UserOperatorFormSchema,
    UserWorkAreasFormSchema
} from '@/features/users/types.ts'
import {StatusBullet} from '@/features/users/components/status-bullet/StatusBullet.tsx'
import {
    getUserDirtyFieldsFromFormValues,
    remapUserToJobDataDefaultValues,
    remapUserToPayload,
    userInterruptionReasonToLabel,
    userStatusToLabel
} from '@/features/users/utils.ts'
import {formatLocaleDate} from '@/dayjs.ts'
import {Badge} from '@components/ui/badge/Badge.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form'
import {UserJobInfoForm} from '@/features/users/components/user-job-info-form/UserJobInfoForm'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {File02Icon} from '@/components/ui/icon/Icon'
import {DetailsField, DetailsFieldsGrid, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {DetailsInformationObservedField} from '@/components/commons/details-information-observed-field/DetailsInformationObservedField'
import {z} from 'zod'
import {workSectorToLabel, workServiceToLabel} from '@/utilities/helpers'
import {Text} from '@/components/ui/text/Text'
import {zodResolver} from '@hookform/resolvers/zod'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {UserDetailInformationContractDataModal} from '@/features/users/components/user-detail-information-contract-data-modal/UserDetailInformationContractDataModal'

type UserJobDataFormValidationKey = keyof z.infer<typeof UserJobInfoFormValidationSchema>

const UserDetailsInformationJobDataSchema = UserJobInfoFormSchema.and(UserWorkAreasFormSchema)
    .and(UserContractualDataFormValidationSchema)
    .and(UserOperatorFormSchema)
type UserDetailsInformationJobDataSchema = z.infer<typeof UserDetailsInformationJobDataSchema>

export const UserDetailsInformationJobData = ({user}: {user: UserExtended}) => {
    const form = useForm<UserDetailsInformationJobDataSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserDetailsInformationJobDataSchema),
        defaultValues: remapUserToJobDataDefaultValues(user)
    })

    return (
        <FormProvider {...form}>
            <UserDetailsInformationJobDataContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationJobDataContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('job-data')
    const form = useFormContext<UserDetailsInformationJobDataSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const setIsContractTypeModalVisible = useUserDetailsStore(state => state.setIsContractTypeModalVisible)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserJobInfoFormValidationSchema
    })

    const userStatusToDateLabel = {
        absent:
            user.temporaryAbsenceStartDate && user.temporaryAbsenceEndDate
                ? t('users:from_to_date', {
                      startDate: formatLocaleDate(user.temporaryAbsenceStartDate, 'L'),
                      endDate: formatLocaleDate(user.temporaryAbsenceEndDate, 'L')
                  })
                : null,
        candidate: user.candidatureDate
            ? t('users:the_date', {date: formatLocaleDate(user.candidatureDate, 'L')})
            : null,
        operative: user.startDate ? t('users:from_date', {date: formatLocaleDate(user.startDate, 'L')}) : null,
        suspended:
            user.suspensionStartDate && user.suspensionEndDate
                ? t('users:from_to_date', {
                      startDate: formatLocaleDate(user.suspensionStartDate, 'L'),
                      endDate: formatLocaleDate(user.suspensionEndDate, 'L')
                  })
                : null,
        terminated: user.interruptionDate
            ? t('users:from_date', {date: formatLocaleDate(user.interruptionDate, 'L')})
            : null
    } as const satisfies Record<UserExtended['status1'], string | null>

    // form values to observe
    const status = useWatch({name: 'status', control: form.control})
    const contractType = useWatch({name: 'contractType', control: form.control})

    const statusDate = userStatusToDateLabel[user.status1]

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    const handleConfirmJobData = () => {
        if (user.status1 != status && !contractType) {
            setIsContractTypeModalVisible(true)
        } else {
            handleSubmit()
        }
    }

    return (
        <>
            <UserDetailInformationContractDataModal user={user} />
            <DetailsFieldsSection
                title={t('users:job_data_title')}
                isDisabled={isDisabledView}
                headingEndSlot={
                    <UserDetailsEditButtons
                        groupName="job-data"
                        isConfirmDisabled={!form.formState.isDirty}
                        onCancel={form.reset}
                        onConfirm={handleConfirmJobData}
                    />
                }
            >
                {isEditModeEnabled ? (
                    <UserJobInfoForm includesAllStatuses isEditMode />
                ) : (
                    <DetailsFieldsGrid>
                        <DetailsField>
                            <Flexbox gap={1.5} align="center">
                                <Text weight="500" size="textMd">
                                    {user.specialization.name}
                                </Text>
                            </Flexbox>
                        </DetailsField>

                        <DetailsField>
                            <Flexbox gap={2} align="center">
                                <StatusBullet status={user.status1} />
                                <Text size="textMd" weight="500">
                                    {t(userStatusToLabel[user.status1])}
                                </Text>
                            </Flexbox>
                            {!!statusDate && (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {statusDate}
                                </Text>
                            )}
                            {user.status1 == 'suspended' && (
                                <Text palette="neutral" shade="500" size="textXs">
                                    {user.suspensionReason}
                                </Text>
                            )}
                            {user.status1 == 'terminated' && user.interruptionReason && (
                                <Text palette="neutral" shade="500" size="textXs">
                                    {t(userInterruptionReasonToLabel[user.interruptionReason])}
                                </Text>
                            )}
                        </DetailsField>

                        {user.specialization.name == 'Amministrativo' ? (
                            <DetailsField>
                                {user.fm07EvaluationAttachment?.url ? (
                                    <ButtonLink
                                        variant="tertiary"
                                        shape="text"
                                        size="lg"
                                        disabled={!user.fm07EvaluationAttachment?.url}
                                        to={user.fm07EvaluationAttachment?.url ?? ''}
                                        target="_blank"
                                    >
                                        <File02Icon />
                                        {t('users:fm07')}
                                    </ButtonLink>
                                ) : (
                                    <Text weight="500" size="textMd">
                                        {t('users:fm07')}
                                    </Text>
                                )}
                                <DetailsInformationObservedField<UserJobDataFormValidationKey>
                                    name="fm07Evaluation"
                                    fieldsToUpdate={user.fieldsToUpdate}
                                    value={
                                        user.fm07Evaluation && (
                                            <Text palette="neutral" shade="500" size="textSm">
                                                {user.fm07Evaluation}
                                            </Text>
                                        )
                                    }
                                />
                            </DetailsField>
                        ) : (
                            <DetailsField>
                                {user.fm08EvaluationAttachment?.url ? (
                                    <ButtonLink
                                        variant="tertiary"
                                        shape="text"
                                        size="lg"
                                        disabled={!user.fm08EvaluationAttachment?.url}
                                        to={user.fm08EvaluationAttachment?.url ?? ''}
                                        target="_blank"
                                    >
                                        <File02Icon />
                                        {t('users:fm08')}
                                    </ButtonLink>
                                ) : (
                                    <Text weight="500" size="textMd">
                                        {t('users:fm08')}
                                    </Text>
                                )}
                                <Flexbox direction="column" gap={1.5}>
                                    <DetailsInformationObservedField<UserJobDataFormValidationKey>
                                        name="fm08Evaluation"
                                        fieldsToUpdate={user.fieldsToUpdate}
                                        hideIcons={['success']}
                                        value={
                                            user.fm08Evaluation && (
                                                <Text palette="neutral" shade="500" size="textSm">
                                                    {user.fm08Evaluation}
                                                </Text>
                                            )
                                        }
                                    />
                                    <Text palette="neutral" shade="500" size="textSm">
                                        {user.fm08EvaluationLastUpdate
                                            ? formatLocaleDate(user.fm08EvaluationLastUpdate, 'L')
                                            : t('users:last_update_not_available')}
                                    </Text>
                                </Flexbox>
                            </DetailsField>
                        )}

                        {user.workSector && (
                            <DetailsField>
                                <Flexbox gap={1.5} direction="column">
                                    <Text weight="500" size="textMd">
                                        {t(workSectorToLabel[user.workSector])}
                                    </Text>
                                    {user.patientAgeRange && (
                                        <Text palette="neutral" shade="500" size="textSm">
                                            {t(workServiceToLabel[user.patientAgeRange])}
                                        </Text>
                                    )}
                                </Flexbox>
                                {user.workAreas && user.workAreas.length >= 1 && (
                                    <Flexbox gap={1.5} style={{flexWrap: 'wrap'}}>
                                        {user.workAreas.map(workArea => (
                                            <Badge shape="rectangle" key={workArea.area.name}>
                                                {workArea.area.name}
                                            </Badge>
                                        ))}
                                    </Flexbox>
                                )}
                            </DetailsField>
                        )}
                    </DetailsFieldsGrid>
                )}
            </DetailsFieldsSection>
        </>
    )
}
